import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import {
  Calendar, FileText, MessageSquare, AlertCircle,
  Loader2, Clock, Stethoscope, ChevronRight,
  Pill, Activity
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function PatientDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [consultations, setConsultations] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState({});

  useEffect(() => {
    if (user?.patient_id) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const patientId = user?.patient_id;

      // Fetch consultations and appointments in parallel
      const [consultationsRes, appointmentsRes] = await Promise.all([
        api.get(`/consultations/patient/${patientId}`).catch(() => ({ data: [] })),
        api.get(`/appointments/patient/${patientId}`).catch(() => ({ data: [] }))
      ]);

      // Get recent consultations (last 3)
      const recentConsultations = (consultationsRes.data || []).slice(0, 3);
      setConsultations(recentConsultations);

      // Get upcoming appointments (future only)
      const now = new Date();
      const upcomingAppointments = (appointmentsRes.data || [])
        .filter(apt => new Date(apt.appointment_date) >= now && apt.status !== 'cancelled')
        .sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date))
        .slice(0, 3);
      setAppointments(upcomingAppointments);

      // Fetch doctor info for consultations and appointments
      const doctorIds = [
        ...new Set([
          ...recentConsultations.map(c => c.doctor_id),
          ...upcomingAppointments.map(a => a.doctor_id)
        ])
      ].filter(Boolean);

      if (doctorIds.length > 0) {
        const doctorPromises = doctorIds.map(id =>
          api.get(`/doctors/${id}`).catch(() => null)
        );
        const doctorResponses = await Promise.all(doctorPromises);
        const doctorMap = {};
        doctorResponses.forEach((res, idx) => {
          if (res?.data) {
            doctorMap[doctorIds[idx]] = res.data;
          }
        });
        setDoctors(doctorMap);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error al cargar información del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-PE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('es-PE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseTreatmentPlan = (treatmentPlan) => {
    if (!treatmentPlan) return null;
    try {
      return JSON.parse(treatmentPlan);
    } catch {
      return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'completed': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed': return 'Confirmada';
      case 'pending': return 'Pendiente';
      case 'cancelled': return 'Cancelada';
      case 'completed': return 'Completada';
      default: return status;
    }
  };

  const quickLinks = [
    {
      name: 'Mi Historia Clínica',
      description: 'Ver mi historia clínica completa',
      icon: FileText,
      href: '/patient/medical-history',
      color: 'bg-primary',
      testId: 'medical-history-link'
    },
    {
      name: 'Mis Citas',
      description: 'Ver y gestionar mis citas médicas',
      icon: Calendar,
      href: '/patient/appointments',
      color: 'bg-orange-500',
      testId: 'appointments-link'
    },
    {
      name: 'Mensajes',
      description: 'Comunicarme con mis médicos',
      icon: MessageSquare,
      href: '/patient/messages',
      color: 'bg-purple-500',
      testId: 'messages-link'
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="dashboard-title">
          Bienvenido, {user?.full_name}
        </h1>
        <p className="text-muted-foreground mt-2">
          Accede a tu información médica y servicios
        </p>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {quickLinks.map((link) => (
          <a key={link.name} href={link.href} data-testid={link.testId}>
            <Card className="p-6 card-hover cursor-pointer h-full">
              <div className="flex flex-col items-start">
                <div className={`w-12 h-12 rounded-lg ${link.color} flex items-center justify-center mb-4`}>
                  <link.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {link.name}
                </h3>
                <p className="text-sm text-muted-foreground">{link.description}</p>
              </div>
            </Card>
          </a>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Consultas Recientes */}
        <Card className="p-6" data-testid="recent-consultations-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Consultas Recientes
            </h2>
            {consultations.length > 0 && (
              <a href="/patient/medical-history" className="text-sm text-primary hover:underline flex items-center">
                Ver todas <ChevronRight className="w-4 h-4" />
              </a>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : consultations.length > 0 ? (
            <div className="space-y-3">
              {consultations.map((consultation) => {
                const doctor = doctors[consultation.doctor_id];
                const plan = parseTreatmentPlan(consultation.treatment_plan);
                const hasMeds = plan?.medicamentos && plan.medicamentos.length > 0;

                return (
                  <div
                    key={consultation.id}
                    className="p-4 rounded-lg border bg-gradient-to-r from-blue-50 to-purple-50 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                          <Clock className="w-4 h-4" />
                          <span>{formatDate(consultation.consultation_date)}</span>
                        </div>
                        <p className="font-medium text-gray-800">{consultation.reason}</p>
                        {doctor && (
                          <div className="flex items-center gap-1 mt-1 text-sm text-blue-600">
                            <Stethoscope className="w-3 h-3" />
                            <span>Dr(a). {doctor.full_name}</span>
                          </div>
                        )}
                        {consultation.diagnosis && (
                          <p className="text-sm text-purple-600 mt-1 font-medium">
                            Dx: {consultation.diagnosis}
                          </p>
                        )}
                        {hasMeds && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
                            <Pill className="w-3 h-3" />
                            <span>{plan.medicamentos.length} medicamento(s) prescritos</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No hay consultas recientes
              </p>
            </div>
          )}
        </Card>

        {/* Próximas Citas */}
        <Card className="p-6" data-testid="upcoming-appointments-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Próximas Citas
            </h2>
            {appointments.length > 0 && (
              <a href="/patient/appointments" className="text-sm text-primary hover:underline flex items-center">
                Ver todas <ChevronRight className="w-4 h-4" />
              </a>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : appointments.length > 0 ? (
            <div className="space-y-3">
              {appointments.map((appointment) => {
                const doctor = doctors[appointment.doctor_id];

                return (
                  <div
                    key={appointment.id}
                    className="p-4 rounded-lg border bg-gradient-to-r from-orange-50 to-yellow-50 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-1 text-orange-600">
                            <Calendar className="w-4 h-4" />
                            <span className="font-semibold">{formatDate(appointment.appointment_date)}</span>
                          </div>
                          <div className="flex items-center gap-1 text-orange-500">
                            <Clock className="w-4 h-4" />
                            <span>{formatTime(appointment.appointment_date)}</span>
                          </div>
                        </div>
                        <p className="font-medium text-gray-800">
                          {appointment.reason || 'Consulta médica'}
                        </p>
                        {doctor && (
                          <div className="flex items-center gap-1 mt-1 text-sm text-blue-600">
                            <Stethoscope className="w-3 h-3" />
                            <span>Dr(a). {doctor.full_name}</span>
                            {doctor.specialization && (
                              <span className="text-gray-400">({doctor.specialization})</span>
                            )}
                          </div>
                        )}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                        {getStatusText(appointment.status)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No hay citas programadas
              </p>
              <a href="/patient/appointments">
                <Button variant="outline" size="sm" className="mt-3">
                  Solicitar cita
                </Button>
              </a>
            </div>
          )}
        </Card>
      </div>

      {/* Health Alerts / Reminders */}
      <Card className="p-6 border-orange-200 bg-orange-50" data-testid="health-alerts-card">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-orange-900">Recordatorios de Salud</h3>
            {consultations.length > 0 ? (
              <div className="mt-2 space-y-1">
                {(() => {
                  const latestConsultation = consultations[0];
                  const plan = parseTreatmentPlan(latestConsultation?.treatment_plan);
                  const hasMeds = plan?.medicamentos && plan.medicamentos.length > 0;

                  if (hasMeds) {
                    return (
                      <p className="text-sm text-orange-700">
                        <Activity className="w-4 h-4 inline mr-1" />
                        Recuerda seguir tu tratamiento con {plan.medicamentos.length} medicamento(s) prescrito(s)
                        en tu última consulta.
                      </p>
                    );
                  }
                  return (
                    <p className="text-sm text-orange-700">
                      No hay recordatorios pendientes en este momento.
                    </p>
                  );
                })()}
              </div>
            ) : (
              <p className="text-sm text-orange-700 mt-1">
                No hay recordatorios pendientes en este momento.
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
